import Vue from 'vue';
import { HttpStatusError } from 'common-errors';
import moment from 'moment';

const getClient = (client) => Vue.api.getClient(client);

const initialState = {
  checked: false,
  session: null,
  storage: {},
  status: '',
  errorCode: null,
  sessionTimeout: null,
  retriesSMS: 0,
};

export const STATUS = {
  CHECKING: 'session.status.checking',
  CHECKED: 'session.status.checked',
  LOADING: 'session.status.loading',
  LOADING_BOPSID: 'session.status.loading.bopsid',
  SUCCESS: 'session.status.success',
  ERROR: 'session.status.error',
};

const mutations = {
  checkingAuth(state) {
    state.status = STATUS.CHECKING;
  },
  checkingFinished(state, session = {}) {
    state.status = STATUS.CHECKED;
    state.checked = true;
    state.session = session || null;
    state.storage = session.storage || {};
  },
  authRequest(state, credentials) {
    state.status = credentials.bopsid ? STATUS.LOADING_BOPSID : STATUS.LOADING;
  },
  authSuccess(state, session) {
    state.status = STATUS.SUCCESS;
    state.session = session;
    state.storage = session.storage || {};
  },
  authError(state, errorCode) {
    state.status = STATUS.ERROR;
    state.errorCode = errorCode;
  },
  logout(state) {
    state.status = '';
    state.session = null;
    state.storage = {};
    state.sessionTimeout = null;
  },
  success(state, updatedUserData) {
    state.status = STATUS.SUCCESS;
    [
      'emailAddress',
      'mobilePhone',
      'agesconPhone',
      'phoneNumber',
      'firstAlternativePhone',
      'telephonePreferente',
      'secondAlternativePhone',
      'preferredPhoneKey',
      'roomLocation',
      'location',
      'isWhatsappAllowed',
      'sala',
      'edificio',
      'planta',
    ]
      .filter((f) => f === 'emailAddress' || f === 'mobilePhone' || updatedUserData[f] != null)
      .forEach((f) => { state.session.user[f] = updatedUserData[f]; });
  //  state.session.user = Object.assign(state.session.user, updatedUserData);
  },
  lastSession(state, lastSession) {
    state.status = STATUS.SUCCESS;
    state.lastAccess = lastSession;
  },
  setUserLocationData(state, locationInfo) {
    state.status = STATUS.SUCCESS;
    state.session.user.sala = locationInfo.sala;
    state.session.user.edificio = locationInfo.edificio;
    state.session.user.planta = locationInfo.planta;
  },
  updateLocation(state, location) {
    state.session.user.location.id = location;
  },
  setSessionTimeout(state, time) {
    state.sessionTimeout = time.getTime();
  },
  updateRetriesSMS(state) {
    state.retriesSMS += 1;
  },
};

function initializeSessionPreferences(commit, session) {
  if (session.preferences != null) {
    Object.entries(session.preferences)
      .forEach((pref) => {
        commit('globalState/setRemoteItem', {
          excludeSync: true,
          key: pref[0],
          value: pref[1],
        }, { root: true });
      });
  }
}

const actions = {
  /**
   * This action is handled by app login to prevent
   * session storage keys setting manually
   */
  async setStorageCredentials({ commit }, credentials) {
    commit('checkingAuth');
    try {
      const session = await getClient('session').fetch(credentials.rest_access, credentials);
      commit('checkingFinished', session);
      return true;
    } catch (error) {
      commit('checkingFinished');
      throw error;
    }
  },
  async check({ commit, state }) {
    const { storage } = state;
    if (!Object.keys(storage).length || !storage.rest_access) {
      commit('checkingFinished');
    } else {
      commit('checkingAuth');
      try {
        const session = await getClient('session').fetch(storage.rest_access, storage);
        initializeSessionPreferences(commit, session);
        commit('checkingFinished', session);
        return true;
      } catch (error) {
        commit('checkingFinished');
        throw error;
      }
    }
    return false;
  },
  async login({ commit }, credentials) {
    commit('authRequest', credentials);
    try {
      const session = await getClient('session').login(credentials);
      if (session.status === 212) {
        commit('authError', 212);
      } else {
        initializeSessionPreferences(commit, session);
        commit('authSuccess', session);
        localStorage.setItem('sessionExpired', 'false');
        commit('setSessionTimeout', new Date());
      }
    } catch (err) {
      let errorCode = 'unkown';
      if (err instanceof HttpStatusError) {
        errorCode = err.statusCode.toString();
      }
      commit('authError', errorCode);
      throw err;
    }
    return true;
  },
  async logout({ commit }) {
    try {
      await getClient('session').logout();
    } catch (ex) {
      console.error('exception ocurrer logout user', ex);
    }
    commit('logout');
    return true;
  },
  async update({ commit }, updatedData) {
    console.log('update session on store', updatedData);
    try {
      commit('success', updatedData);
    } catch (ex) {
      const errEX = ex;
      commit('error', errEX);
    }
  },
  async trustedThirdPartyAuth({ commit }, tempCode) {
    commit('authRequest', {});
    try {
      const session = await getClient('session').trustedThirdPartyAuth(tempCode);
      commit('authSuccess', session);
      return session;
    } catch (err) {
      let errorCode = 'unkown';
      if (err instanceof HttpStatusError) {
        errorCode = err.statusCode.toString();
      }
      commit('authError', errorCode);
      throw err;
    }
  },
  async refreshUserLocationData({ commit, state }) {
    try {
      const user = await getClient('user').getUserFromSession(state.session);
      const locationInfo = {};
      [locationInfo.sala, locationInfo.edificio, locationInfo.planta] = [
        user.sala, user.edificio, user.planta];
      commit('setUserLocationData', locationInfo);
    } catch (err) {
      commit('error', err.message);
      throw err;
    }
  },
  async lastSession({ commit }) {
    try {
      const sessions = await getClient('registerSession').list();
      const { list } = sessions;
      if (list != null && list[0] != null) {
        const date = moment(list[0].sessions[0].fecha);
        commit('lastSession', date);
      }
    } catch (err) {
      commit('error', err.message);
      throw err;
    }
  },
  async updateLocation({ commit }, location) {
    console.log('update location on store', location);
    try {
      commit('updateLocation', location);
    } catch (ex) {
      const errEX = ex;
      commit('error', errEX);
    }
  },
  updateTimeout({ commit }, timeout) {
    commit('setSessionTimeout', timeout);
  },
  updateRetriesSMS({ commit }) {
    commit('updateRetriesSMS');
  },
};

export { NAMED_ROLES } from '@/plugins/api-client/models/permission-role';
const getters = {
  checked: (state) => state.checked,
  isSignedIn: (state) => state.session != null && state.session.user != null,
  user: (state) => state.session.user,
  storage: (state) => state.session.storage,
  errorCode: (state) => (state.status === STATUS.ERROR ? state.errorCode : null),
  // eslint-disable-next-line max-len
  hasRole: (state) => (rol) => (state.session != null && state.session.user != null ? state.session.user.hasRole(rol.authCode, rol.accessLevel) : null),
  lastAccess: (state) => state.lastAccess,
  sessionTimeout: (state) => state.sessionTimeout,
  retriesSMS: (state) => state.retriesSMS,
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
};
